// src/components/admin/page-management/section-forms/BlogPostsForm.js
import React, { useState } from 'react';
import { Info, Type, Layout, Palette, Settings, Eye, FileText, MousePointer, Play, Database , SpaceIcon} from 'lucide-react';
import ColorPicker from '../../shared/ColorPicker';

const BlogPostsForm = ({ content, updateContent }) => {
  const [activeTab, setActiveTab] = useState('content');
  
  const tabs = [
    { id: 'content', label: 'Content', icon: FileText },
    { id: 'layout', label: 'Layout', icon: Layout },
    { id: 'design', label: 'Design', icon: Palette },
    { id: 'display', label: 'Display', icon: Eye },
    { id: 'spacing', label: 'Spacing', icon: SpaceIcon },
    { id: 'advanced', label: 'Advanced', icon: Settings }
  ];

  const TabButton = ({ tab, isActive, onClick }) => {
    const Icon = tab.icon;
    return (
      <button
        type="button"
        onClick={() => onClick(tab.id)}
        className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
          isActive
            ? 'bg-blue-600 text-white shadow-md'
            : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-800 border border-gray-200'
        }`}
      >
        <Icon className="h-4 w-4 mr-2" />
        {tab.label}
      </button>
    );
  };

  const FormSection = ({ title, children, description, icon: Icon, variant = 'default' }) => (
    <div className={`bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200 ${
      variant === 'info' ? 'border-blue-200 bg-blue-50' : ''
    }`}>
      <div className="flex items-center mb-4">
        {Icon && <Icon className={`h-5 w-5 mr-3 ${variant === 'info' ? 'text-blue-600' : 'text-blue-600'}`} />}
        <div>
          <h4 className="font-semibold text-gray-900">{title}</h4>
          {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
        </div>
      </div>
      {children}
    </div>
  );

  const InputField = ({ label, value, onChange, placeholder, type = "text", min, max, step, rows, description, options }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {type === 'select' ? (
        <select
          value={value}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          value={value}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
          rows={rows || 3}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          placeholder={placeholder}
          min={min}
          max={max}
          step={step}
        />
      )}
      {description && <p className="text-xs text-gray-500">{description}</p>}
    </div>
  );

  const ColorField = ({ label, color, onChange, description }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="flex items-center space-x-3">
        <ColorPicker color={color} onChange={onChange} />
        <div 
          className="w-8 h-8 rounded-lg border-2 border-gray-200 shadow-sm"
          style={{ backgroundColor: color }}
        />
      </div>
      {description && <p className="text-xs text-gray-500">{description}</p>}
    </div>
  );

  const CheckboxField = ({ label, checked, onChange, description }) => (
    <div className="flex items-start space-x-3">
      <div className="flex items-center h-5">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
        />
      </div>
      <div className="flex-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
      </div>
    </div>
  );

  const renderContentTab = () => (
    <div className="space-y-6">
      <FormSection 
        title="Section Title" 
        description="Configure the main heading for your blog section"
        icon={Type}
      >
        <div className="space-y-6">
          <CheckboxField
            label="Show Section Title"
            checked={content?.showTitle !== false}
            onChange={(e) => updateContent('showTitle', e.target.checked)}
            description="Display a title above the blog posts grid"
          />
          
          {content?.showTitle !== false && (
            <div className="space-y-4 pl-6 border-l-2 border-blue-200">
              <InputField
                label="Title Text"
                value={content?.title || 'Latest From Our Blog'}
                onChange={(e) => updateContent('title', e.target.value)}
                placeholder="Enter section title"
              />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InputField
                  label="Title Size"
                  type="select"
                  value={content?.titleSize || 'text-3xl'}
                  onChange={(e) => updateContent('titleSize', e.target.value)}
                  options={[
                    { value: 'text-2xl', label: 'Small' },
                    { value: 'text-3xl', label: 'Medium' },
                    { value: 'text-4xl', label: 'Large' }
                  ]}
                />
                
                <InputField
                  label="Title Alignment"
                  type="select"
                  value={content?.titleAlignment || 'text-center'}
                  onChange={(e) => updateContent('titleAlignment', e.target.value)}
                  options={[
                    { value: 'text-left', label: 'Left' },
                    { value: 'text-center', label: 'Center' },
                    { value: 'text-right', label: 'Right' }
                  ]}
                />
                
                <InputField
                  label="Font Weight"
                  type="select"
                  value={content?.titleFontWeight || 'font-bold'}
                  onChange={(e) => updateContent('titleFontWeight', e.target.value)}
                  options={[
                    { value: 'font-normal', label: 'Normal' },
                    { value: 'font-medium', label: 'Medium' },
                    { value: 'font-semibold', label: 'Semibold' },
                    { value: 'font-bold', label: 'Bold' }
                  ]}
                />
              </div>
              
              <ColorField
                label="Title Color"
                color={content?.titleColor || '#1f2937'}
                onChange={(color) => updateContent('titleColor', color)}
              />
            </div>
          )}
        </div>
      </FormSection>

      <FormSection 
        title="Subtitle" 
        description="Add an optional subtitle below the main title"
      >
        <div className="space-y-4">
          <InputField
            label="Subtitle Text"
            type="textarea"
            value={content?.subtitle || ''}
            onChange={(e) => updateContent('subtitle', e.target.value)}
            placeholder="Enter a subtitle (leave empty to hide)"
            rows={2}
            description="Optional subtitle that appears below the main title"
          />
          
          {content?.subtitle && (
            <ColorField
              label="Subtitle Color"
              color={content?.subtitleColor || '#4b5563'}
              onChange={(color) => updateContent('subtitleColor', color)}
            />
          )}
        </div>
      </FormSection>

      <FormSection 
        title="Posts Configuration" 
        description="Control how many posts to display and their content"
        icon={FileText}
      >
        <div className="space-y-6">
          <InputField
            label="Number of Posts to Display"
            type="number"
            min="1"
            max="12"
            value={content?.postsCount || 3}
            onChange={(e) => updateContent('postsCount', parseInt(e.target.value, 10))}
            description="How many blog posts to show in this section"
          />

          <div>
            <h5 className="font-medium text-gray-900 mb-4">Post Content Options</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CheckboxField
                label="Show Category Tags"
                checked={content?.showCategory !== false}
                onChange={(e) => updateContent('showCategory', e.target.checked)}
                description="Display category labels on each post"
              />
              
              <CheckboxField
                label="Show Publish Date"
                checked={content?.showDate !== false}
                onChange={(e) => updateContent('showDate', e.target.checked)}
                description="Display when each post was published"
              />
              
              <CheckboxField
                label="Show Post Excerpt"
                checked={content?.showExcerpt !== false}
                onChange={(e) => updateContent('showExcerpt', e.target.checked)}
                description="Show a preview of the post content"
              />
              
              <CheckboxField
                label="Show Read More Link"
                checked={content?.showReadMore !== false}
                onChange={(e) => updateContent('showReadMore', e.target.checked)}
                description="Add a link to read the full post"
              />
            </div>
          </div>

          {content?.showDate !== false && (
            <InputField
              label="Date Format"
              type="select"
              value={content?.dateFormat || 'MM/DD/YYYY'}
              onChange={(e) => updateContent('dateFormat', e.target.value)}
              options={[
                { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' }
              ]}
            />
          )}

          {content?.showExcerpt !== false && (
            <InputField
              label="Excerpt Length"
              type="select"
              value={content?.excerptLines || 2}
              onChange={(e) => updateContent('excerptLines', parseInt(e.target.value, 10))}
              options={[
                { value: 1, label: '1 Line' },
                { value: 2, label: '2 Lines' },
                { value: 3, label: '3 Lines' },
                { value: 4, label: '4 Lines' }
              ]}
              description="How many lines of preview text to show"
            />
          )}
        </div>
      </FormSection>
    </div>
  );

  const renderLayoutTab = () => (
    <div className="space-y-6">
      <FormSection 
        title="Grid Layout" 
        description="Configure how posts are arranged on different screen sizes"
        icon={Layout}
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InputField
              label="Mobile Columns"
              type="select"
              value={content?.postsPerRow?.mobile || 1}
              onChange={(e) => updateContent('postsPerRow', {
                ...content?.postsPerRow,
                mobile: parseInt(e.target.value, 10)
              })}
              options={[
                { value: 1, label: '1 Column' },
                { value: 2, label: '2 Columns' }
              ]}
              description="Layout on phones"
            />
            
            <InputField
              label="Tablet Columns"
              type="select"
              value={content?.postsPerRow?.tablet || 2}
              onChange={(e) => updateContent('postsPerRow', {
                ...content?.postsPerRow,
                tablet: parseInt(e.target.value, 10)
              })}
              options={[
                { value: 1, label: '1 Column' },
                { value: 2, label: '2 Columns' },
                { value: 3, label: '3 Columns' }
              ]}
              description="Layout on tablets"
            />
            
            <InputField
              label="Desktop Columns"
              type="select"
              value={content?.postsPerRow?.desktop || 3}
              onChange={(e) => updateContent('postsPerRow', {
                ...content?.postsPerRow,
                desktop: parseInt(e.target.value, 10)
              })}
              options={[
                { value: 1, label: '1 Column' },
                { value: 2, label: '2 Columns' },
                { value: 3, label: '3 Columns' },
                { value: 4, label: '4 Columns' }
              ]}
              description="Layout on desktop"
            />
          </div>
          
          <InputField
            label="Space Between Cards"
            value={content?.spaceBetweenCards || '2rem'}
            onChange={(e) => updateContent('spaceBetweenCards', e.target.value)}
            placeholder="e.g., 2rem, 32px"
            description="Gap between blog post cards"
          />
        </div>
      </FormSection>

      <FormSection 
        title="Featured Images" 
        description="Configure how post images are displayed"
        icon={Eye}
      >
        <div className="space-y-4">
          <InputField
            label="Image Aspect Ratio"
            value={content?.imageAspectRatio || '60%'}
            onChange={(e) => updateContent('imageAspectRatio', e.target.value)}
            placeholder="e.g., 60%, 50%, 75%"
            description="Height of images relative to width. Lower = shorter, higher = taller"
          />
          
          <InputField
            label="Fallback Image URL"
            value={content?.placeholderImage || '/images/placeholder-blog.jpg'}
            onChange={(e) => updateContent('placeholderImage', e.target.value)}
            placeholder="URL for default image when post has no featured image"
            description="Image shown when a post doesn't have a featured image"
          />
        </div>
      </FormSection>
    </div>
  );

  const renderDesignTab = () => (
    <div className="space-y-6">
      <FormSection 
        title="Card Style" 
        description="Choose the visual style for blog post cards"
        icon={Palette}
      >
        <div className="space-y-6">
          <InputField
            label="Card Style"
            type="select"
            value={content?.cardStyle || 'shadow'}
            onChange={(e) => updateContent('cardStyle', e.target.value)}
            options={[
              { value: 'shadow', label: 'Shadow' },
              { value: 'outline', label: 'Outline' },
              { value: 'flat', label: 'Flat' }
            ]}
            description="Visual style for each blog post card"
          />
          
          <InputField
            label="Card Border Radius"
            value={content?.cardBorderRadius || '0.5rem'}
            onChange={(e) => updateContent('cardBorderRadius', e.target.value)}
            placeholder="e.g., 0.5rem, 8px, 0"
            description="Roundness of card corners"
          />
          
          {content?.cardStyle === 'shadow' && (
            <InputField
              label="Shadow Size"
              type="select"
              value={content?.cardShadowSize || 'shadow-xl'}
              onChange={(e) => updateContent('cardShadowSize', e.target.value)}
              options={[
                { value: 'shadow', label: 'Small' },
                { value: 'shadow-md', label: 'Medium' },
                { value: 'shadow-lg', label: 'Large' },
                { value: 'shadow-xl', label: 'Extra Large' }
              ]}
            />
          )}
          
          <ColorField
            label="Card Background Color"
            color={content?.cardBgColor || '#ffffff'}
            onChange={(color) => updateContent('cardBgColor', color)}
            description="Background color for each blog post card"
          />
          
          <CheckboxField
            label="Enable Hover Effects"
            checked={content?.cardHoverEffect !== false}
            onChange={(e) => updateContent('cardHoverEffect', e.target.checked)}
            description="Add smooth animations when hovering over cards"
          />
        </div>
      </FormSection>

      <FormSection 
        title="Text Colors" 
        description="Customize colors for different text elements"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {content?.showCategory !== false && (
            <ColorField
              label="Category Tag Color"
              color={content?.categoryColor || '#2563eb'}
              onChange={(color) => updateContent('categoryColor', color)}
            />
          )}
          
          {content?.showReadMore !== false && (
            <ColorField
              label="Read More Link Color"
              color={content?.readMoreColor || '#2563eb'}
              onChange={(color) => updateContent('readMoreColor', color)}
            />
          )}
        </div>
      </FormSection>

      {content?.showReadMore !== false && (
        <FormSection 
          title="Read More Link" 
          description="Customize the read more link appearance"
        >
          <div className="space-y-4">
            <InputField
              label="Read More Text"
              value={content?.readMoreText || 'Read More'}
              onChange={(e) => updateContent('readMoreText', e.target.value)}
              placeholder="Link text"
            />
            
            <CheckboxField
              label="Show Arrow Icon"
              checked={content?.readMoreIcon !== false}
              onChange={(e) => updateContent('readMoreIcon', e.target.checked)}
              description="Display an arrow icon next to the read more text"
            />
          </div>
        </FormSection>
      )}
    </div>
  );

  const renderDisplayTab = () => (
    <div className="space-y-6">
      <FormSection 
        title="View All Button" 
        description="Add a button to link to your complete blog page"
        icon={MousePointer}
      >
        <div className="space-y-6">
          <CheckboxField
            label="Show View All Button"
            checked={content?.showViewAllButton !== false}
            onChange={(e) => updateContent('showViewAllButton', e.target.checked)}
            description="Display a button that links to your full blog page"
          />
          
          {content?.showViewAllButton !== false && (
            <div className="space-y-4 pl-6 border-l-2 border-blue-200">
              <InputField
                label="Button Text"
                value={content?.viewAllButtonText || 'View All Posts'}
                onChange={(e) => updateContent('viewAllButtonText', e.target.value)}
                placeholder="Button label"
              />
              
              <InputField
                label="Button Link"
                value={content?.viewAllButtonLink || '/blog'}
                onChange={(e) => updateContent('viewAllButtonLink', e.target.value)}
                placeholder="URL to your blog page"
                description="Where users go when they click the button"
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ColorField
                  label="Button Background"
                  color={content?.viewAllButtonColor || '#033568'}
                  onChange={(color) => updateContent('viewAllButtonColor', color)}
                />
                
                <ColorField
                  label="Button Text Color"
                  color={content?.viewAllButtonTextColor || '#ffffff'}
                  onChange={(color) => updateContent('viewAllButtonTextColor', color)}
                />
              </div>
            </div>
          )}
        </div>
      </FormSection>

      <FormSection 
        title="Animations" 
        description="Add smooth animations when posts appear"
        icon={Play}
      >
        <div className="space-y-4">
          <CheckboxField
            label="Enable Animations"
            checked={content?.animationEnabled !== false}
            onChange={(e) => updateContent('animationEnabled', e.target.checked)}
            description="Add fade-in animations when posts come into view"
          />
          
          {content?.animationEnabled !== false && (
            <InputField
              label="Animation Delay Between Cards"
              type="number"
              min="0"
              max="500"
              step="50"
              value={content?.animationDelay || 150}
              onChange={(e) => updateContent('animationDelay', parseInt(e.target.value, 10))}
              description="Delay in milliseconds between each card animation"
            />
          )}
        </div>
      </FormSection>
    </div>
  );

  const renderSpacingTab = () => (
    <div className="space-y-6">
      <FormSection 
        title="Section Spacing" 
        description="Control the space around the entire blog section"
        icon={SpaceIcon}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField
            label="Top Margin"
            value={content?.marginTop || '2rem'}
            onChange={(e) => updateContent('marginTop', e.target.value)}
            placeholder="e.g., 2rem, 32px"
            description="Space above the section"
          />
          
          <InputField
            label="Bottom Margin"
            value={content?.marginBottom || '8rem'}
            onChange={(e) => updateContent('marginBottom', e.target.value)}
            placeholder="e.g., 8rem, 128px"
            description="Space below the section"
          />
          
          <InputField
            label="Top Padding"
            value={content?.paddingTop || '1rem'}
            onChange={(e) => updateContent('paddingTop', e.target.value)}
            placeholder="e.g., 1rem, 16px"
            description="Internal space at the top"
          />
          
          <InputField
            label="Bottom Padding"
            value={content?.paddingBottom || '1rem'}
            onChange={(e) => updateContent('paddingBottom', e.target.value)}
            placeholder="e.g., 1rem, 16px"
            description="Internal space at the bottom"
          />
        </div>
      </FormSection>
    </div>
  );

  const renderAdvancedTab = () => (
    <div className="space-y-6">
      <FormSection 
        title="API Configuration" 
        description="Advanced settings for developers"
        icon={Database}
        variant="info"
      >
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <Info className="h-4 w-4 text-amber-600 mr-2" />
              <span className="text-sm font-medium text-amber-800">Developer Settings</span>
            </div>
            <p className="text-sm text-amber-700">
              These settings control how blog posts are fetched from your website. 
              Most users should leave these at their default values unless you have specific technical requirements.
            </p>
          </div>
          
          <InputField
            label="API Endpoint"
            value={content?.apiPath || '/api/blog/posts?limit=6&sort=newest&status=published'}
            onChange={(e) => updateContent('apiPath', e.target.value)}
            placeholder="API endpoint URL"
            description="Customize query parameters to control which posts are fetched (limit, sort, filter, etc.)"
          />
        </div>
      </FormSection>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <div className="bg-green-100 p-2 rounded-lg mr-3">
            <FileText className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Blog Posts Section</h3>
            <p className="text-gray-600">Configure how your blog posts are displayed</p>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 p-1 bg-gray-100 rounded-lg">
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              tab={tab}
              isActive={activeTab === tab.id}
              onClick={setActiveTab}
            />
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="transition-all duration-300">
        {activeTab === 'content' && renderContentTab()}
        {activeTab === 'layout' && renderLayoutTab()}
        {activeTab === 'design' && renderDesignTab()}
        {activeTab === 'display' && renderDisplayTab()}
        {activeTab === 'spacing' && renderSpacingTab()}
        {activeTab === 'advanced' && renderAdvancedTab()}
      </div>
    </div>
  );
};

export default BlogPostsForm;



// // src/components/admin/page-management/section-forms/BlogPostsForm.js
// import React from 'react';
// import { Info } from 'lucide-react';
// import ColorPicker from '../../shared/ColorPicker';

// const BlogPostsForm = ({ content, updateContent }) => {
//   return (
//     <div className="space-y-6">
//       <h3 className="text-lg font-semibold">Blog Posts Section</h3>
      
//       {/* Title Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Title Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showTitle !== false}
//               onChange={(e) => updateContent('showTitle', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Title</span>
//           </label>
//         </div>
        
//         {content?.showTitle !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Title</label>
//               <input
//                 type="text"
//                 value={content?.title || 'Latest From Our Blog'}
//                 onChange={(e) => updateContent('title', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Title Color</label>
//               <ColorPicker
//                 color={content?.titleColor || '#1f2937'}
//                 onChange={(color) => updateContent('titleColor', color)}
//               />
//             </div>
            
//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">Title Size</label>
//                 <select
//                   value={content?.titleSize || 'text-3xl'}
//                   onChange={(e) => updateContent('titleSize', e.target.value)}
//                   className="w-full p-2 border rounded"
//                 >
//                   <option value="text-2xl">Small</option>
//                   <option value="text-3xl">Medium</option>
//                   <option value="text-4xl">Large</option>
//                 </select>
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Title Alignment</label>
//                 <select
//                   value={content?.titleAlignment || 'text-center'}
//                   onChange={(e) => updateContent('titleAlignment', e.target.value)}
//                   className="w-full p-2 border rounded"
//                 >
//                   <option value="text-left">Left</option>
//                   <option value="text-center">Center</option>
//                   <option value="text-right">Right</option>
//                 </select>
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Title Font Weight</label>
//                 <select
//                   value={content?.titleFontWeight || 'font-bold'}
//                   onChange={(e) => updateContent('titleFontWeight', e.target.value)}
//                   className="w-full p-2 border rounded"
//                 >
//                   <option value="font-normal">Normal</option>
//                   <option value="font-medium">Medium</option>
//                   <option value="font-semibold">Semibold</option>
//                   <option value="font-bold">Bold</option>
//                 </select>
//               </div>
//             </div>
//           </>
//         )}
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Subtitle (Optional)</label>
//           <textarea
//             value={content?.subtitle || ''}
//             onChange={(e) => updateContent('subtitle', e.target.value)}
//             className="w-full p-2 border rounded"
//             rows="2"
//             placeholder="Enter a subtitle (leave empty to hide)"
//           />
//         </div>
        
//         {content?.subtitle && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Subtitle Color</label>
//             <ColorPicker
//               color={content?.subtitleColor || '#4b5563'}
//               onChange={(color) => updateContent('subtitleColor', color)}
//             />
//           </div>
//         )}
//       </div>
      
//       {/* Content Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Content Settings</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Number of Posts to Display</label>
//           <input
//             type="number"
//             min="1"
//             max="12"
//             value={content?.postsCount || 3}
//             onChange={(e) => updateContent('postsCount', parseInt(e.target.value, 10))}
//             className="w-32 p-2 border rounded"
//           />
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Mobile Columns</label>
//             <select
//               value={content?.postsPerRow?.mobile || 1}
//               onChange={(e) => updateContent('postsPerRow', {
//                 ...content?.postsPerRow,
//                 mobile: parseInt(e.target.value, 10)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={1}>1 Column</option>
//               <option value={2}>2 Columns</option>
//             </select>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Tablet Columns</label>
//             <select
//               value={content?.postsPerRow?.tablet || 2}
//               onChange={(e) => updateContent('postsPerRow', {
//                 ...content?.postsPerRow,
//                 tablet: parseInt(e.target.value, 10)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={1}>1 Column</option>
//               <option value={2}>2 Columns</option>
//               <option value={3}>3 Columns</option>
//             </select>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Desktop Columns</label>
//             <select
//               value={content?.postsPerRow?.desktop || 3}
//               onChange={(e) => updateContent('postsPerRow', {
//                 ...content?.postsPerRow,
//                 desktop: parseInt(e.target.value, 10)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={1}>1 Column</option>
//               <option value={2}>2 Columns</option>
//               <option value={3}>3 Columns</option>
//               <option value={4}>4 Columns</option>
//             </select>
//           </div>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Space Between Cards</label>
//           <input
//             type="text"
//             value={content?.spaceBetweenCards || '2rem'}
//             onChange={(e) => updateContent('spaceBetweenCards', e.target.value)}
//             className="w-full p-2 border rounded"
//             placeholder="e.g., 2rem, 32px"
//           />
//         </div>
//       </div>
      
//       {/* Card Style Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Card Style</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Card Style</label>
//           <select
//             value={content?.cardStyle || 'shadow'}
//             onChange={(e) => updateContent('cardStyle', e.target.value)}
//             className="w-full p-2 border rounded"
//           >
//             <option value="shadow">Shadow</option>
//             <option value="outline">Outline</option>
//             <option value="flat">Flat</option>
//           </select>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Card Border Radius</label>
//           <input
//             type="text"
//             value={content?.cardBorderRadius || '0.5rem'}
//             onChange={(e) => updateContent('cardBorderRadius', e.target.value)}
//             className="w-full p-2 border rounded"
//             placeholder="e.g., 0.5rem, 8px"
//           />
//         </div>
        
//         {content?.cardStyle === 'shadow' && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Shadow Size</label>
//             <select
//               value={content?.cardShadowSize || 'shadow-xl'}
//               onChange={(e) => updateContent('cardShadowSize', e.target.value)}
//               className="w-full p-2 border rounded"
//             >
//               <option value="shadow">Small</option>
//               <option value="shadow-md">Medium</option>
//               <option value="shadow-lg">Large</option>
//               <option value="shadow-xl">Extra Large</option>
//             </select>
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Card Background Color</label>
//           <ColorPicker
//             color={content?.cardBgColor || '#ffffff'}
//             onChange={(color) => updateContent('cardBgColor', color)}
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.cardHoverEffect !== false}
//               onChange={(e) => updateContent('cardHoverEffect', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Hover Effect</span>
//           </label>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Featured Image Aspect Ratio</label>
//           <input
//             type="text"
//             value={content?.imageAspectRatio || '60%'}
//             onChange={(e) => updateContent('imageAspectRatio', e.target.value)}
//             className="w-full p-2 border rounded"
//             placeholder="e.g., 60%, 50%, 75%"
//           />
//           <p className="text-xs text-gray-500 mt-1">
//             Lower percentages make shorter images, higher percentages make taller images
//           </p>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Placeholder Image URL</label>
//           <input
//             type="text"
//             value={content?.placeholderImage || '/images/placeholder-blog.jpg'}
//             onChange={(e) => updateContent('placeholderImage', e.target.value)}
//             className="w-full p-2 border rounded"
//           />
//         </div>
//       </div>
      
//       {/* Post Content Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Post Content Settings</h4>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="flex items-center">
//               <input
//                 type="checkbox"
//                 checked={content?.showCategory !== false}
//                 onChange={(e) => updateContent('showCategory', e.target.checked)}
//                 className="mr-2"
//               />
//               <span className="text-sm">Show Category</span>
//             </label>
//           </div>
//           <div>
//             <label className="flex items-center">
//               <input
//                 type="checkbox"
//                 checked={content?.showDate !== false}
//                 onChange={(e) => updateContent('showDate', e.target.checked)}
//                 className="mr-2"
//               />
//               <span className="text-sm">Show Date</span>
//             </label>
//           </div>
//         </div>
        
//         {content?.showCategory !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Category Text Color</label>
//             <ColorPicker
//               color={content?.categoryColor || '#2563eb'}
//               onChange={(color) => updateContent('categoryColor', color)}
//             />
//           </div>
//         )}
        
//         {content?.showDate !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Date Format</label>
//             <select
//               value={content?.dateFormat || 'MM/DD/YYYY'}
//               onChange={(e) => updateContent('dateFormat', e.target.value)}
//               className="w-full p-2 border rounded"
//             >
//               <option value="MM/DD/YYYY">MM/DD/YYYY</option>
//               <option value="DD/MM/YYYY">DD/MM/YYYY</option>
//             </select>
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showExcerpt !== false}
//               onChange={(e) => updateContent('showExcerpt', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Excerpt</span>
//           </label>
//         </div>
        
//         {content?.showExcerpt !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Excerpt Length (lines)</label>
//             <select
//               value={content?.excerptLines || 2}
//               onChange={(e) => updateContent('excerptLines', parseInt(e.target.value, 10))}
//               className="w-full p-2 border rounded"
//             >
//               <option value={1}>1 Line</option>
//               <option value={2}>2 Lines</option>
//               <option value={3}>3 Lines</option>
//               <option value={4}>4 Lines</option>
//             </select>
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showReadMore !== false}
//               onChange={(e) => updateContent('showReadMore', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show "Read More" Link</span>
//           </label>
//         </div>
        
//         {content?.showReadMore !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">"Read More" Text</label>
//               <input
//                 type="text"
//                 value={content?.readMoreText || 'Read More'}
//                 onChange={(e) => updateContent('readMoreText', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">"Read More" Color</label>
//               <ColorPicker
//                 color={content?.readMoreColor || '#2563eb'}
//                 onChange={(color) => updateContent('readMoreColor', color)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.readMoreIcon !== false}
//                   onChange={(e) => updateContent('readMoreIcon', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Show Arrow Icon</span>
//               </label>
//             </div>
//           </>
//         )}
//       </div>
      
//       {/* "View All" Button Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">"View All" Button Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showViewAllButton !== false}
//               onChange={(e) => updateContent('showViewAllButton', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show "View All" Button</span>
//           </label>
//         </div>
        
//         {content?.showViewAllButton !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Text</label>
//               <input
//                 type="text"
//                 value={content?.viewAllButtonText || 'View All Posts'}
//                 onChange={(e) => updateContent('viewAllButtonText', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Link</label>
//               <input
//                 type="text"
//                 value={content?.viewAllButtonLink || '/blog'}
//                 onChange={(e) => updateContent('viewAllButtonLink', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">Button Color</label>
//                 <ColorPicker
//                   color={content?.viewAllButtonColor || '#033568'}
//                   onChange={(color) => updateContent('viewAllButtonColor', color)}
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Button Text Color</label>
//                 <ColorPicker
//                   color={content?.viewAllButtonTextColor || '#ffffff'}
//                   onChange={(color) => updateContent('viewAllButtonTextColor', color)}
//                 />
//               </div>
//             </div>
//           </>
//         )}
//       </div>
      
//       {/* Spacing Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Spacing Settings</h4>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Margin Top</label>
//             <input
//               type="text"
//               value={content?.marginTop || '2rem'}
//               onChange={(e) => updateContent('marginTop', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 2rem, 32px"
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Margin Bottom</label>
//             <input
//               type="text"
//               value={content?.marginBottom || '8rem'}
//               onChange={(e) => updateContent('marginBottom', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 8rem, 128px"
//             />
//           </div>
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Padding Top</label>
//             <input
//               type="text"
//               value={content?.paddingTop || '1rem'}
//               onChange={(e) => updateContent('paddingTop', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 1rem, 16px"
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Padding Bottom</label>
//             <input
//               type="text"
//               value={content?.paddingBottom || '1rem'}
//               onChange={(e) => updateContent('paddingBottom', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 1rem, 16px"
//             />
//           </div>
//         </div>
//       </div>
      
//       {/* Animation Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Animation Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.animationEnabled !== false}
//               onChange={(e) => updateContent('animationEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Animations</span>
//           </label>
//         </div>
        
//         {content?.animationEnabled !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Animation Delay Between Cards (ms)</label>
//             <input
//               type="number"
//               min="0"
//               max="500"
//               step="50"
//               value={content?.animationDelay || 150}
//               onChange={(e) => updateContent('animationDelay', parseInt(e.target.value, 10))}
//               className="w-32 p-2 border rounded"
//             />
//           </div>
//         )}
//       </div>
      
//       {/* API Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <div className="flex justify-between items-center mb-4">
//           <h4 className="font-medium">API Settings</h4>
//           <div className="text-blue-500">
//             <Info size={16} />
//           </div>
//         </div>
        
//         <p className="text-sm text-gray-600 mb-4">
//           These settings are for advanced users. Most users should leave these at their default values.
//         </p>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">API Path</label>
//           <input
//             type="text"
//             value={content?.apiPath || '/api/blog/posts?limit=6&sort=newest&status=published'}
//             onChange={(e) => updateContent('apiPath', e.target.value)}
//             className="w-full p-2 border rounded"
//           />
//           <p className="text-xs text-gray-500 mt-1">
//             You can customize the query parameters to control which posts are fetched.
//           </p>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default BlogPostsForm;